import { useState, type FormEvent } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { useAddComment } from '../api';
import type { Comment } from '../types';
import { MarkdownBody } from './MarkdownBody';

interface Props {
  issueId: string;
  comments: Comment[];
}

export function CommentsThread({ issueId, comments }: Props) {
  const [body, setBody] = useState('');
  const addComment = useAddComment(issueId);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await addComment.mutateAsync({ body });
      setBody('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post comment');
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Conversation</h2>

      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <CommentCard key={c.id} comment={c} />
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="space-y-2">
        <Textarea
          rows={4}
          placeholder="Add a comment…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="comment-input"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={addComment.isPending || !body.trim()}>
            {addComment.isPending ? 'Posting…' : 'Post comment'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function CommentCard({ comment }: { comment: Comment }) {
  const isCustomer = comment.author === 'customer';
  return (
    <li
      data-author={comment.author}
      className={
        isCustomer
          ? 'rounded-md border border-primary/20 bg-primary/5 p-3'
          : 'rounded-md border bg-muted/40 p-3'
      }
    >
      <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        {isCustomer ? (
          <span className="font-medium text-primary">Customer</span>
        ) : (
          // The badge keeps the staff/customer distinction readable at a glance;
          // the name says who on the Raptor side replied. Absent a name (a Gitea
          // user with neither full_name nor login), the badge stands alone.
          <>
            <Badge variant="secondary">Staff</Badge>
            {comment.authorName && (
              <span className="font-medium text-foreground" data-testid="comment-author-name">
                {comment.authorName}
              </span>
            )}
          </>
        )}
        <span>·</span>
        <span>{formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}</span>
      </div>
      <MarkdownBody>{comment.body}</MarkdownBody>
    </li>
  );
}
